import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import api from '../lib/api';
import { fundraiseUrl } from '../lib/fundraiseUrl';
import { toast } from 'sonner';
import { BarChart3, Mountain, DollarSign, Users, Settings, Plus, Pencil, Trash2, Building2, Award, Footprints, Upload, X, Image, Mail, Phone, CheckCircle, Clock, XCircle, MapPin, ChevronUp, ChevronDown, Heart, Link2, Eye, EyeOff, Send, Activity, AlertTriangle } from 'lucide-react';

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [challengeStats, setChallengeStats] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [walkerTypes, setWalkerTypes] = useState([]);
  const [achievementLevels, setAchievementLevels] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [corpSponsors, setCorpSponsors] = useState([]);
  const [sponsorshipLevels, setSponsorshipLevels] = useState([]);
  const [sponsorInquiries, setSponsorInquiries] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  const loadAll = () => {
    Promise.all([
      api.get('/admin/stats'),
      api.get('/challenges/all'),
      api.get('/walker-types'),
      api.get('/achievement-levels'),
      api.get('/admin/users'),
      api.get('/admin/config'),
      api.get('/corporate-sponsors').catch(() => ({ data: [] })),
      api.get('/sponsorship-levels').catch(() => ({ data: [] })),
      api.get('/sponsor-inquiries').catch(() => ({ data: [] })),
      api.get('/admin/stats/by-challenge').catch(() => ({ data: [] })),
      api.get('/admin/teams').catch(() => ({ data: [] })),
      api.get('/admin/supporters').catch(() => ({ data: [] })),
    ]).then(([s, c, wt, al, u, cfg, cs, sl, si, chs, tm, sup]) => {
      setStats(s.data);
      setChallenges(c.data);
      setWalkerTypes(wt.data);
      setAchievementLevels(al.data);
      setUsers(u.data);
      setConfig(cfg.data);
      setCorpSponsors(cs.data);
      setSponsorshipLevels(sl.data);
      setSponsorInquiries(si.data);
      setChallengeStats(chs.data);
      setTeams(tm.data);
      setSupporters(sup.data);
    }).catch(() => toast.error('Failed to load admin data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="container-app py-8 md:py-12" data-testid="admin-page">
      <h1 className="text-2xl md:text-3xl font-bold text-stone-900 mb-8">Admin Console</h1>

      <Tabs defaultValue="stats">
        <TabsList className="flex flex-wrap bg-stone-100 rounded-xl p-1 mb-6 gap-1">
          <TabsTrigger value="stats" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-stats"><BarChart3 className="w-3 h-3 mr-1" /> Stats</TabsTrigger>
          <TabsTrigger value="challenges" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-challenges"><Mountain className="w-3 h-3 mr-1" /> Challenges</TabsTrigger>
          <TabsTrigger value="walker-types" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-walker-types"><Footprints className="w-3 h-3 mr-1" /> Registration</TabsTrigger>
          <TabsTrigger value="achievements" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-achievements"><Award className="w-3 h-3 mr-1" /> Achievements</TabsTrigger>
          <TabsTrigger value="users" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-users"><Users className="w-3 h-3 mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="teams" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-teams"><Users className="w-3 h-3 mr-1" /> Teams</TabsTrigger>
          <TabsTrigger value="supporters" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-supporters"><Heart className="w-3 h-3 mr-1" /> Supporters</TabsTrigger>
          <TabsTrigger value="corporate" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-corporate"><Building2 className="w-3 h-3 mr-1" /> Corporate</TabsTrigger>
          <TabsTrigger value="email" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-email"><Send className="w-3 h-3 mr-1" /> Email</TabsTrigger>
          <TabsTrigger value="config" className="rounded-lg text-xs sm:text-sm" data-testid="admin-tab-config"><Settings className="w-3 h-3 mr-1" /> Config</TabsTrigger>
        </TabsList>

        {/* Stats */}
        <TabsContent value="stats">
          <MultiChallengeMigrationButton />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: 'Total Walkers', value: stats?.total_users, icon: Users },
              { label: 'Total Teams', value: stats?.total_teams, icon: Users },
              { label: 'Total Distance', value: `${stats?.total_distance_km} km`, icon: Mountain },
              { label: 'Total Steps', value: stats?.total_steps?.toLocaleString(), icon: Footprints },
              { label: 'Total Pledged', value: `$${stats?.total_pledged}`, icon: DollarSign },
              { label: 'Corporate Sponsors', value: stats?.total_corporate_sponsors, icon: Building2 },
            ].map(s => (
              <Card key={s.label} className="bg-white rounded-2xl border border-stone-100">
                <CardContent className="p-5">
                  <s.icon className="w-5 h-5 text-orange-600 mb-2" />
                  <p className="text-2xl font-bold text-stone-900">{s.value}</p>
                  <p className="text-xs text-stone-400 uppercase tracking-wider font-medium mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Stats by Challenge */}
          {challengeStats.length > 0 && (
            <Card className="bg-white rounded-2xl border border-stone-100 mt-6" data-testid="admin-stats-by-challenge">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-stone-900 mb-4">Stats by Challenge</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left py-2 px-3 text-xs text-stone-400 font-medium uppercase">Challenge</th>
                        <th className="text-left py-2 px-3 text-xs text-stone-400 font-medium uppercase">Status</th>
                        <th className="text-right py-2 px-3 text-xs text-stone-400 font-medium uppercase">Walkers</th>
                        <th className="text-right py-2 px-3 text-xs text-stone-400 font-medium uppercase">Teams</th>
                        <th className="text-right py-2 px-3 text-xs text-stone-400 font-medium uppercase">Pledged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {challengeStats.map(cs => (
                        <tr key={cs.challenge_id} className="border-b border-stone-50">
                          <td className="py-2.5 px-3 font-medium text-stone-900">{cs.challenge_name}</td>
                          <td className="py-2.5 px-3">
                            <Badge className={`rounded-full text-xs ${cs.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-400'}`}>
                              {cs.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-2.5 px-3 text-right text-stone-600">{cs.walkers}</td>
                          <td className="py-2.5 px-3 text-right text-stone-600">{cs.teams}</td>
                          <td className="py-2.5 px-3 text-right font-medium text-stone-900">${cs.pledged}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Challenges */}
        <TabsContent value="challenges">
          <ChallengesAdmin challenges={challenges} onRefresh={loadAll} />
        </TabsContent>

        {/* Walker Types */}
        <TabsContent value="walker-types">
          <WalkerTypesAdmin walkerTypes={walkerTypes} onRefresh={loadAll} />
        </TabsContent>

        {/* Achievement Levels */}
        <TabsContent value="achievements">
          <AchievementLevelsAdmin levels={achievementLevels} onRefresh={loadAll} />
        </TabsContent>

        {/* Users */}
        <TabsContent value="users">
          <UsersAdmin users={users} walkerTypes={walkerTypes} challenges={challenges} teams={teams} onRefresh={loadAll} />
        </TabsContent>
        <TabsContent value="teams">
          <TeamsAdmin teams={teams} users={users} challenges={challenges} onRefresh={loadAll} />
        </TabsContent>
        <TabsContent value="supporters">
          <SupportersAdmin supporters={supporters} onRefresh={loadAll} />
        </TabsContent>

        {/* Corporate */}
        <TabsContent value="corporate">
          <CorporateSponsorsAdmin 
            levels={sponsorshipLevels} 
            sponsors={corpSponsors} 
            onRefresh={loadAll} 
          />
        </TabsContent>

        {/* Email */}
        <TabsContent value="email">
          <EmailAdmin />
        </TabsContent>

        {/* Config */}
        <TabsContent value="config">
          <ConfigAdmin config={config} onRefresh={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChallengesAdmin({ challenges, onRefresh }) {
  const [form, setForm] = useState({ name: '', description: '', total_distance_km: '', milestones: [], is_active: true, send_postcards: false });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [milestoneStr, setMilestoneStr] = useState('');
  const [uploadingMap, setUploadingMap] = useState(null);
  const [uploadingMilestonePhoto, setUploadingMilestonePhoto] = useState(null);
  const mapInputRef = useRef(null);
  // Postcard state
  const [postcardOpen, setPostcardOpen] = useState(false);
  const [postcardChallengeId, setPostcardChallengeId] = useState(null);
  const [postcardForm, setPostcardForm] = useState({ title: '', distance_km: '', subject_line: '', body: '' });
  const [editingPostcard, setEditingPostcard] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(null);

  const openCreate = () => {
    setForm({ name: '', description: '', total_distance_km: '', milestones: [], is_active: true, send_postcards: false });
    setMilestoneStr('');
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (ch) => {
    setForm({ 
      name: ch.name, 
      description: ch.description, 
      total_distance_km: String(ch.total_distance_km), 
      milestones: ch.milestones || [],
      is_active: ch.is_active !== false,
      route_map_url: ch.route_map_url || null,
      send_postcards: ch.send_postcards || false
    });
    setMilestoneStr(ch.milestones?.map(m => `${m.distance_km}:${m.title}:${m.description || ''}`).join('\n') || '');
    setEditing(ch.id);
    setOpen(true);
  };

  const parseMilestones = (str) => {
    return str.split('\n').filter(l => l.trim()).map(l => {
      const parts = l.split(':');
      return { distance_km: parseFloat(parts[0]) || 0, title: parts[1]?.trim() || '', description: parts.slice(2).join(':').trim() || '' };
    });
  };

  const handleSave = async () => {
    if (form.description.length < 50) {
      toast.error('Description must be at least 50 characters');
      return;
    }
    if (form.description.length > 2000) {
      toast.error('Description must be at most 2000 characters');
      return;
    }
    const payload = {
      name: form.name,
      description: form.description,
      total_distance_km: parseFloat(form.total_distance_km),
      milestones: parseMilestones(milestoneStr),
      is_active: form.is_active,
      send_postcards: form.send_postcards,
    };
    try {
      if (editing) {
        await api.put(`/challenges/${editing}`, payload);
        toast.success('Challenge updated');
      } else {
        await api.post('/challenges', payload);
        toast.success('Challenge created');
      }
      setOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleToggleActive = async (ch) => {
    try {
      await api.put(`/challenges/${ch.id}`, { is_active: !ch.is_active });
      toast.success(ch.is_active ? 'Challenge deactivated' : 'Challenge activated');
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  const handleFeature = async (ch) => {
    try {
      await api.put('/admin/leaderboard-featured', { challenge_id: ch.featured_on_leaderboard ? null : ch.id });
      toast.success(ch.featured_on_leaderboard ? 'Mobile leaderboard reset to newest active challenge' : `Mobile leaderboard now shows ${ch.name}`);
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Delete this challenge?')) return;
    try {
      await api.delete(`/challenges/${id}`);
      toast.success('Challenge deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const handleMove = async (index, direction) => {
    const sorted = [...challenges];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;
    [sorted[index], sorted[targetIndex]] = [sorted[targetIndex], sorted[index]];
    const ordered_ids = sorted.map(c => c.id);
    try {
      await api.post('/challenges/reorder', { ordered_ids });
      onRefresh();
    } catch { toast.error('Failed to reorder'); }
  };

  // Postcard handlers
  const openAddPostcard = (challengeId) => {
    setPostcardChallengeId(challengeId);
    setPostcardForm({ title: '', distance_km: '', subject_line: '', body: '' });
    setEditingPostcard(null);
    setPostcardOpen(true);
  };
  const openEditPostcard = (challengeId, pc) => {
    setPostcardChallengeId(challengeId);
    setPostcardForm({ title: pc.title, distance_km: String(pc.distance_km), subject_line: pc.subject_line, body: pc.body });
    setEditingPostcard(pc.id);
    setPostcardOpen(true);
  };
  const handleSavePostcard = async () => {
    const payload = { title: postcardForm.title, distance_km: parseFloat(postcardForm.distance_km) || 0, subject_line: postcardForm.subject_line, body: postcardForm.body };
    try {
      if (editingPostcard) {
        await api.put(`/challenges/${postcardChallengeId}/postcards/${editingPostcard}`, payload);
        toast.success('Postcard updated');
      } else {
        await api.post(`/challenges/${postcardChallengeId}/postcards`, payload);
        toast.success('Postcard added');
      }
      setPostcardOpen(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save postcard'); }
  };
  const handleDeletePostcard = async (challengeId, postcardId) => {
    if (!window.confirm('Delete this postcard?')) return;
    try {
      await api.delete(`/challenges/${challengeId}/postcards/${postcardId}`);
      toast.success('Postcard deleted');
      onRefresh();
    } catch { toast.error('Failed to delete postcard'); }
  };
  const handleTogglePostcards = async (ch) => {
    try {
      await api.put(`/challenges/${ch.id}`, { send_postcards: !ch.send_postcards });
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };
  const handleUploadAttachment = async (challengeId, postcardId, file) => {
    setUploadingAttachment(postcardId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/challenges/${challengeId}/postcards/${postcardId}/attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Attachment uploaded');
      onRefresh();
    } catch { toast.error('Failed to upload attachment'); }
    finally { setUploadingAttachment(null); }
  };

  const handleUploadMap = async (challengeId, file) => {
    setUploadingMap(challengeId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/challenges/${challengeId}/route-map`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Route map uploaded');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload map');
    } finally {
      setUploadingMap(null);
    }
  };

  const handleUploadMilestonePhoto = async (challengeId, milestoneIndex, file) => {
    setUploadingMilestonePhoto(`${challengeId}-${milestoneIndex}`);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/challenges/${challengeId}/milestones/${milestoneIndex}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingMilestonePhoto(null);
    }
  };

  const handleDeleteMilestonePhoto = async (challengeId, milestoneIndex) => {
    if (!window.confirm('Remove this milestone photo?')) return;
    try {
      await api.delete(`/challenges/${challengeId}/milestones/${milestoneIndex}/image`);
      toast.success('Photo removed');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const activeCount = challenges.filter(c => c.is_active !== false).length;

  return (
    <Card className="bg-white rounded-2xl border border-stone-100">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-stone-900">Challenges ({challenges.length})</h3>
            <p className="text-xs text-stone-400">{activeCount} active, {challenges.length - activeCount} inactive</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-add-challenge-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? 'Edit Challenge' : 'New Challenge'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Name (unique)</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g., Nairobi to Naivasha" className="mt-1 rounded-xl" data-testid="admin-challenge-name" />
                </div>
                <div>
                  <Label className="text-sm">Description (50-2000 characters)</Label>
                  <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Describe the journey, what walkers will experience..." rows={4} className="mt-1 rounded-xl" data-testid="admin-challenge-desc" />
                  <p className="text-[10px] text-stone-400 mt-1">{form.description.length}/2000 characters</p>
                </div>
                <div>
                  <Label className="text-sm">Total Distance (km)</Label>
                  <Input type="number" value={form.total_distance_km} onChange={e => setForm(f => ({...f, total_distance_km: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-challenge-distance" />
                </div>
                <div>
                  <Label className="text-sm">Milestones (one per line: distance:title:description)</Label>
                  <Textarea value={milestoneStr} onChange={e => setMilestoneStr(e.target.value)} placeholder="10:Checkpoint 1:A scenic viewpoint with acacia trees\n25:Rest Stop:Local village with fresh water" rows={5} className="mt-1 rounded-xl font-mono text-xs" data-testid="admin-challenge-milestones" />
                  <p className="text-[10px] text-stone-400 mt-1">Format: distance_km:title:what walker sees/smells/hears</p>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50">
                  <div>
                    <p className="text-sm font-medium text-stone-900">Active</p>
                    <p className="text-xs text-stone-400">Show this challenge to walkers</p>
                  </div>
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({...f, is_active: v}))} data-testid="admin-challenge-active" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50">
                  <div>
                    <p className="text-sm font-medium text-stone-900">Send a Postcard?</p>
                    <p className="text-xs text-stone-400">Enable surprise email postcards at distance milestones</p>
                  </div>
                  <Switch checked={form.send_postcards} onCheckedChange={(v) => setForm(f => ({...f, send_postcards: v}))} data-testid="admin-challenge-postcards-toggle" />
                </div>
                <Button onClick={handleSave} className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-challenge-save-btn">
                  {editing ? 'Update' : 'Create'} Challenge
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-3">
          {challenges.map((ch, idx) => (
            <div key={ch.id} className={`rounded-xl ${ch.is_active !== false ? 'bg-stone-50' : 'bg-stone-100 opacity-60'}`} data-testid={`admin-challenge-${ch.id}`}>
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" disabled={idx === 0} onClick={() => handleMove(idx, -1)} data-testid={`challenge-move-up-${ch.id}`}>
                      <ChevronUp className="w-4 h-4 text-stone-500" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" disabled={idx === challenges.length - 1} onClick={() => handleMove(idx, 1)} data-testid={`challenge-move-down-${ch.id}`}>
                      <ChevronDown className="w-4 h-4 text-stone-500" />
                    </Button>
                  </div>
                  <Switch 
                    checked={ch.is_active !== false} 
                    onCheckedChange={() => handleToggleActive(ch)}
                    data-testid={`toggle-challenge-${ch.id}`}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-stone-900">{ch.name}</p>
                      {ch.is_active === false && <Badge className="bg-stone-200 text-stone-500 text-[10px] rounded-full">Inactive</Badge>}
                      {ch.featured_on_leaderboard && <Badge className="bg-amber-100 text-amber-800 text-[10px] rounded-full" data-testid={`challenge-featured-badge-${ch.id}`}>On mobile leaderboard</Badge>}
                    </div>
                    <p className="text-xs text-stone-400">{ch.total_distance_km} km &middot; {ch.milestones?.length || 0} milestones &middot; Order: {ch.display_order || idx + 1}</p>
                  </div>
                </div>
                <div className="flex gap-1 items-center">
                  <Button
                    variant={ch.featured_on_leaderboard ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleFeature(ch)}
                    className="rounded-full text-xs h-8"
                    data-testid={`challenge-feature-leaderboard-${ch.id}`}
                  >
                    {ch.featured_on_leaderboard ? 'Shown on mobile leaderboard' : 'Show on mobile leaderboard'}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(ch)} className="rounded-full"><Pencil className="w-4 h-4 text-stone-400" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(ch.id)} className="rounded-full"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                </div>
              </div>
              {/* Route Map + Milestone Photos */}
              <div className="px-3 pb-3 space-y-2">
                {/* Route Map */}
                <div className="flex items-center gap-3 p-2 rounded-lg bg-white border border-stone-100">
                  <div className="shrink-0">
                    {ch.route_map_url ? (
                      <img src={ch.route_map_url} alt="Route map" className="w-12 h-12 rounded object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-stone-100 flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-stone-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-stone-700">Route Map</p>
                    <p className="text-[10px] text-stone-400">{ch.route_map_url ? 'Uploaded' : 'No map uploaded'}</p>
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      const f = e.target.files?.[0]; if (f) handleUploadMap(ch.id, f);
                    }} />
                    <Badge className="bg-orange-50 text-orange-700 hover:bg-orange-100 cursor-pointer text-[10px] rounded-full">
                      {uploadingMap === ch.id ? 'Uploading...' : ch.route_map_url ? 'Replace' : 'Upload'}
                    </Badge>
                  </label>
                </div>
                {/* Milestone Photos */}
                {ch.milestones?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-stone-400 font-medium uppercase tracking-wider px-1">Milestone Photos</p>
                    {ch.milestones.map((m, mi) => {
                      const currentImg = m.image_url || (m.images && m.images[0]);
                      return (
                      <div key={mi} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-stone-100">
                        <div className="flex gap-1 shrink-0">
                          {currentImg ? (
                            <img src={currentImg} alt="" className="w-10 h-10 rounded object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-stone-100 flex items-center justify-center">
                              <Mountain className="w-4 h-4 text-stone-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-stone-700 truncate">{m.title || m.name}</p>
                          <p className="text-[10px] text-stone-400">{m.distance_km}km</p>
                        </div>
                        <label className="cursor-pointer shrink-0">
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                            const f = e.target.files?.[0]; if (f) handleUploadMilestonePhoto(ch.id, mi, f);
                            e.target.value = '';
                          }} />
                          <Badge className="bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer text-[10px] rounded-full" data-testid={`milestone-upload-${ch.id}-${mi}`}>
                            {uploadingMilestonePhoto === `${ch.id}-${mi}` ? '...' : (currentImg ? 'Replace' : '+ Photo')}
                          </Badge>
                        </label>
                        {currentImg && (
                          <button
                            type="button"
                            onClick={() => handleDeleteMilestonePhoto(ch.id, mi)}
                            className="text-stone-300 hover:text-red-500 shrink-0"
                            title="Remove photo"
                            data-testid={`milestone-delete-${ch.id}-${mi}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
                {/* Postcards Section */}
                {ch.send_postcards && (
                  <div className="space-y-1 mt-2">
                    <div className="flex items-center justify-between px-1">
                      <p className="text-[10px] text-stone-400 font-medium uppercase tracking-wider">Postcards ({(ch.postcards || []).length})</p>
                      <button onClick={() => openAddPostcard(ch.id)} className="text-[10px] text-orange-600 hover:text-orange-700 font-medium" data-testid={`add-postcard-${ch.id}`}>+ Add Postcard</button>
                    </div>
                    {(ch.postcards || []).map(pc => (
                      <div key={pc.id} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-stone-100">
                        <Mail className="w-4 h-4 text-orange-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-stone-700 truncate">{pc.title}</p>
                          <p className="text-[10px] text-stone-400">{pc.distance_km}km &middot; {pc.attachment_url ? 'Has attachment' : 'No attachment'}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <label className="cursor-pointer">
                            <input type="file" className="hidden" onChange={(e) => {
                              const f = e.target.files?.[0]; if (f) handleUploadAttachment(ch.id, pc.id, f);
                            }} />
                            <Badge className="bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer text-[10px] rounded-full">
                              {uploadingAttachment === pc.id ? '...' : pc.attachment_url ? 'Replace' : 'Attach'}
                            </Badge>
                          </label>
                          <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={() => openEditPostcard(ch.id, pc)}><Pencil className="w-3 h-3 text-stone-400" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={() => handleDeletePostcard(ch.id, pc.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                        </div>
                      </div>
                    ))}
                    {(ch.postcards || []).length === 0 && (
                      <p className="text-[10px] text-stone-300 italic px-1">No postcards defined yet</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Postcard Add/Edit Dialog */}
        <Dialog open={postcardOpen} onOpenChange={setPostcardOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editingPostcard ? 'Edit Postcard' : 'Add Postcard'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-sm">Title</Label><Input value={postcardForm.title} onChange={e => setPostcardForm(f => ({...f, title: e.target.value}))} placeholder="e.g., Halfway There!" className="mt-1 rounded-xl" data-testid="postcard-title" /></div>
              <div><Label className="text-sm">Distance (km)</Label><Input type="number" value={postcardForm.distance_km} onChange={e => setPostcardForm(f => ({...f, distance_km: e.target.value}))} placeholder="e.g., 50" className="mt-1 rounded-xl" data-testid="postcard-distance" /></div>
              <div><Label className="text-sm">Subject Line</Label><Input value={postcardForm.subject_line} onChange={e => setPostcardForm(f => ({...f, subject_line: e.target.value}))} placeholder="Email subject line" className="mt-1 rounded-xl" data-testid="postcard-subject" /></div>
              <div><Label className="text-sm">Body</Label><Textarea value={postcardForm.body} onChange={e => setPostcardForm(f => ({...f, body: e.target.value}))} placeholder="Email body content..." rows={4} className="mt-1 rounded-xl" data-testid="postcard-body" /></div>
              <p className="text-[10px] text-stone-400">Attachment can be uploaded after saving the postcard.</p>
              <Button onClick={handleSavePostcard} className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="postcard-save-btn">{editingPostcard ? 'Update' : 'Add'} Postcard</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function WalkerTypesAdmin({ walkerTypes, onRefresh }) {
  const [form, setForm] = useState({ name: '', cost_usd: '', display_order: '' });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const openCreate = () => { setForm({ name: '', cost_usd: '', display_order: '' }); setEditing(null); setOpen(true); };
  const openEdit = (wt) => { setForm({ name: wt.name, cost_usd: String(wt.cost_usd), display_order: String(wt.display_order) }); setEditing(wt.id); setOpen(true); };

  const handleSave = async () => {
    const payload = { name: form.name, cost_usd: parseFloat(form.cost_usd), display_order: Math.max(1, parseInt(form.display_order) || 1) };
    try {
      if (editing) { await api.put(`/walker-types/${editing}`, payload); toast.success('Updated'); }
      else { await api.post('/walker-types', payload); toast.success('Created'); }
      setOpen(false); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/walker-types/${id}`); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed'); }
  };

  return (
    <Card className="bg-white rounded-2xl border border-stone-100">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-stone-900">Registration Levels ({walkerTypes.length})</h3>
            <p className="text-xs text-stone-400 mt-0.5">Registration fee levels for walkers</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-add-walker-type-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? 'Edit Registration Level' : 'New Registration Level'}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-sm">Name</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-wt-name" /></div>
                <div><Label className="text-sm">Cost (USD)</Label><Input type="number" value={form.cost_usd} onChange={e => setForm(f => ({...f, cost_usd: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-wt-cost" /></div>
                <div><Label className="text-sm">Display Order</Label><Input type="number" min="1" value={form.display_order} onChange={e => setForm(f => ({...f, display_order: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-wt-order" /></div>
                <Button onClick={handleSave} className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-wt-save-btn">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-2">
          {walkerTypes.map(wt => (
            <div key={wt.id} className="flex items-center justify-between p-3 rounded-xl bg-stone-50" data-testid={`admin-wt-${wt.id}`}>
              <div>
                <p className="text-sm font-medium text-stone-900">{wt.name} - ${wt.cost_usd}</p>
                <p className="text-xs text-stone-400">Order: {wt.display_order}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(wt)} className="rounded-full"><Pencil className="w-4 h-4 text-stone-400" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(wt.id)} className="rounded-full"><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AchievementLevelsAdmin({ levels, onRefresh }) {
  const [form, setForm] = useState({ total_amount_usd: '', achievement: '', swag: '', display_order: '' });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const openCreate = () => { setForm({ total_amount_usd: '', achievement: '', swag: '', display_order: '' }); setEditing(null); setOpen(true); };
  const openEdit = (al) => { setForm({ total_amount_usd: String(al.total_amount_usd), achievement: al.achievement, swag: al.swag, display_order: String(al.display_order) }); setEditing(al.id); setOpen(true); };

  const handleSave = async () => {
    const payload = { total_amount_usd: parseFloat(form.total_amount_usd), achievement: form.achievement, swag: form.swag, display_order: Math.max(1, parseInt(form.display_order) || 1) };
    try {
      if (editing) { await api.put(`/achievement-levels/${editing}`, payload); toast.success('Updated'); }
      else { await api.post('/achievement-levels', payload); toast.success('Created'); }
      setOpen(false); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/achievement-levels/${id}`); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed'); }
  };

  return (
    <Card className="bg-white rounded-2xl border border-stone-100">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-stone-900">Achievement Levels ({levels.length})</h3>
            <p className="text-xs text-stone-400 mt-0.5">Based on total amount raised (walker fee + teammates + supporters)</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-add-achievement-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? 'Edit Achievement Level' : 'New Achievement Level'}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-sm">Total Amount (USD)</Label><Input type="number" value={form.total_amount_usd} onChange={e => setForm(f => ({...f, total_amount_usd: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-al-amount" /></div>
                <div><Label className="text-sm">Achievement</Label><Input value={form.achievement} onChange={e => setForm(f => ({...f, achievement: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-al-achievement" /></div>
                <div><Label className="text-sm">Thank You Swag</Label><Input value={form.swag} onChange={e => setForm(f => ({...f, swag: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-al-swag" /></div>
                <div><Label className="text-sm">Display Order</Label><Input type="number" min="1" value={form.display_order} onChange={e => setForm(f => ({...f, display_order: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-al-order" /></div>
                <Button onClick={handleSave} className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-al-save-btn">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-2">
          {levels.map(al => (
            <div key={al.id} className="flex items-center justify-between p-3 rounded-xl bg-stone-50" data-testid={`admin-al-${al.id}`}>
              <div>
                <p className="text-sm font-medium text-stone-900">${al.total_amount_usd.toLocaleString()} - {al.achievement}</p>
                <p className="text-xs text-stone-400">Swag: {al.swag}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(al)} className="rounded-full"><Pencil className="w-4 h-4 text-stone-400" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(al.id)} className="rounded-full"><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ConfigAdmin({ config, onRefresh }) {
  const [form, setForm] = useState({
    name: config.name || '',
    app_short_name: config.app_short_name || '',
    primary_color: config.primary_color || '',
    secondary_color: config.secondary_color || '',
    steps_per_km: config.steps_per_km || 1300,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [appLogoUrl, setAppLogoUrl] = useState(config.app_logo_url || '');
  const fileInputRef = useRef(null);

  const handleSave = async () => {
    const stepsVal = parseInt(form.steps_per_km) || 1300;
    if (stepsVal < 1100 || stepsVal > 1600) {
      toast.error('Steps per km must be between 1100 and 1600');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/config', { ...form, steps_per_km: stepsVal });
      toast.success('Config updated');
      onRefresh();
    } catch { toast.error('Failed to update config'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/config/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Cache-bust by appending a timestamp
      setAppLogoUrl(`${res.data.app_logo_url}?t=${Date.now()}`);
      toast.success('App logo uploaded');
      onRefresh();
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="bg-white rounded-2xl border border-stone-100 max-w-lg">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-stone-900 mb-4">App Configuration</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">App Name</Label>
            <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-config-name" />
          </div>
          <div>
            <Label className="text-sm">App Short Name</Label>
            <Input
              value={form.app_short_name}
              onChange={e => setForm(f => ({...f, app_short_name: e.target.value.slice(0, 12)}))}
              placeholder="Kenya Walk"
              maxLength={12}
              className="mt-1 rounded-xl"
              data-testid="admin-config-short-name"
            />
            <p className="text-[10px] text-stone-400 mt-1">Shown under the app icon when a user adds the site to their phone's home screen. Keep it ≤ 12 characters.</p>
          </div>
          <div>
            <Label className="text-sm">App Logo (192 × 192)</Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="w-16 h-16 rounded-2xl border border-stone-200 bg-stone-50 overflow-hidden flex items-center justify-center shrink-0">
                {appLogoUrl ? (
                  <img src={appLogoUrl} alt="App icon" className="w-full h-full object-cover" data-testid="admin-config-logo-preview" />
                ) : (
                  <Image className="w-6 h-6 text-stone-300" />
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="admin-config-logo-input"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 border border-stone-200"
                  data-testid="admin-config-logo-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-1.5" /> {uploading ? 'Uploading...' : (appLogoUrl ? 'Replace Logo' : 'Upload Logo')}
                </Button>
                <p className="text-[10px] text-stone-400 mt-1">Square PNG, 192 × 192 px. Used when a user adds the site to their home screen.</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-sm">Primary Color</Label>
            <Input value={form.primary_color} onChange={e => setForm(f => ({...f, primary_color: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-config-primary" />
          </div>
          <div>
            <Label className="text-sm">Secondary Color</Label>
            <Input value={form.secondary_color} onChange={e => setForm(f => ({...f, secondary_color: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-config-secondary" />
          </div>
          <div>
            <Label className="text-sm">Steps per Kilometer</Label>
            <Input type="number" min={1100} max={1600} value={form.steps_per_km} onChange={e => setForm(f => ({...f, steps_per_km: e.target.value}))} className="mt-1 rounded-xl" data-testid="admin-config-steps-per-km" />
            <p className="text-[10px] text-stone-400 mt-1">Used to convert steps to km when logging activity (min 1100, max 1600)</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-config-save-btn">
            {saving ? 'Saving...' : 'Save Config'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CorporateSponsorsAdmin({ levels, sponsors, onRefresh }) {
  // Sponsorship Levels state
  const [levelForm, setLevelForm] = useState({ name: '', max_sponsors: '', display_order: '' });
  const [editingLevel, setEditingLevel] = useState(null);
  const [levelOpen, setLevelOpen] = useState(false);

  // Sponsors state
  const [sponsorForm, setSponsorForm] = useState({ name: '', level_id: '', website_url: '' });
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);

  // Level CRUD
  const openCreateLevel = () => { setLevelForm({ name: '', max_sponsors: '', display_order: '' }); setEditingLevel(null); setLevelOpen(true); };
  const openEditLevel = (l) => { setLevelForm({ name: l.name, max_sponsors: String(l.max_sponsors || ''), display_order: String(l.display_order || 0) }); setEditingLevel(l.id); setLevelOpen(true); };

  const handleSaveLevel = async () => {
    const payload = { name: levelForm.name, max_sponsors: levelForm.max_sponsors ? parseInt(levelForm.max_sponsors) : null, display_order: Math.max(1, parseInt(levelForm.display_order) || 1) };
    try {
      if (editingLevel) { await api.put(`/sponsorship-levels/${editingLevel}`, payload); toast.success('Level updated'); }
      else { await api.post('/sponsorship-levels', payload); toast.success('Level created'); }
      setLevelOpen(false); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteLevel = async (id) => {
    if (!window.confirm('Delete this sponsorship level?')) return;
    try { await api.delete(`/sponsorship-levels/${id}`); toast.success('Deleted'); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  // Sponsor CRUD
  const getSponsorCount = (levelId) => sponsors.filter(s => s.level_id === levelId).length;
  const getAvailableLevel = () => {
    for (const l of levels) {
      if (!l.max_sponsors || getSponsorCount(l.id) < l.max_sponsors) return l.id;
    }
    return levels[0]?.id || '';
  };
  const openCreateSponsor = () => { setSponsorForm({ name: '', level_id: getAvailableLevel(), website_url: '' }); setEditingSponsor(null); setSponsorOpen(true); };
  const openEditSponsor = (s) => { setSponsorForm({ name: s.name, level_id: s.level_id, website_url: s.website_url || '' }); setEditingSponsor(s.id); setSponsorOpen(true); };

  const handleSaveSponsor = async () => {
    const payload = { name: sponsorForm.name, level_id: sponsorForm.level_id, website_url: sponsorForm.website_url || null };
    try {
      if (editingSponsor) { await api.put(`/corporate-sponsors/${editingSponsor}`, payload); toast.success('Sponsor updated'); }
      else { await api.post('/corporate-sponsors', payload); toast.success('Sponsor created'); }
      setSponsorOpen(false); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSponsor = async (id) => {
    if (!window.confirm('Delete this sponsor?')) return;
    try { await api.delete(`/corporate-sponsors/${id}`); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleUploadLogo = async (sponsorId, file) => {
    setUploading(sponsorId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/corporate-sponsors/${sponsorId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteLogo = async (sponsorId) => {
    if (!window.confirm('Remove logo?')) return;
    try {
      await api.delete(`/corporate-sponsors/${sponsorId}/logo`);
      toast.success('Logo removed');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const getLevelName = (levelId) => levels.find(l => l.id === levelId)?.name || 'Unknown';

  return (
    <div className="space-y-6">
      {/* Sponsorship Levels */}
      <Card className="bg-white rounded-2xl border border-stone-100">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-stone-900">Sponsorship Levels ({levels.length})</h3>
              <p className="text-xs text-stone-400 mt-0.5">Define sponsorship tiers (e.g., Title, Gold, Silver)</p>
            </div>
            <Dialog open={levelOpen} onOpenChange={setLevelOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateLevel} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-add-sponsor-level-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Level
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingLevel ? 'Edit Level' : 'New Sponsorship Level'}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Level Name</Label>
                    <Input value={levelForm.name} onChange={e => setLevelForm(f => ({...f, name: e.target.value}))} placeholder="e.g., Title Sponsor" className="mt-1 rounded-xl" data-testid="admin-level-name" />
                  </div>
                  <div>
                    <Label className="text-sm">Max Sponsors (leave empty for unlimited)</Label>
                    <Input type="number" value={levelForm.max_sponsors} onChange={e => setLevelForm(f => ({...f, max_sponsors: e.target.value}))} placeholder="e.g., 1" className="mt-1 rounded-xl" data-testid="admin-level-max" />
                  </div>
                  <div>
                    <Label className="text-sm">Display Order</Label>
                    <Input type="number" min="1" value={levelForm.display_order} onChange={e => setLevelForm(f => ({...f, display_order: e.target.value}))} placeholder="1" className="mt-1 rounded-xl" data-testid="admin-level-order" />
                  </div>
                  <Button onClick={handleSaveLevel} className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-level-save-btn">{editingLevel ? 'Update' : 'Create'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-2">
            {levels.length === 0 ? (
              <p className="text-stone-400 text-center py-4">No sponsorship levels yet. Add one to get started.</p>
            ) : (
              levels.map(l => {
                const sponsorCount = sponsors.filter(s => s.level_id === l.id).length;
                return (
                  <div key={l.id} className="flex items-center justify-between p-3 rounded-xl bg-stone-50" data-testid={`admin-level-${l.id}`}>
                    <div>
                      <p className="text-sm font-medium text-stone-900">{l.name}</p>
                      <p className="text-xs text-stone-400">
                        {sponsorCount} sponsor{sponsorCount !== 1 ? 's' : ''}
                        {l.max_sponsors ? ` / max ${l.max_sponsors}` : ' / unlimited'}
                        {' · Order: '}{l.display_order}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditLevel(l)} className="rounded-full"><Pencil className="w-4 h-4 text-stone-400" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteLevel(l.id)} className="rounded-full"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Corporate Sponsors */}
      <Card className="bg-white rounded-2xl border border-stone-100">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-stone-900">Corporate Sponsors ({sponsors.length})</h3>
              <p className="text-xs text-stone-400 mt-0.5">Manage sponsor names and logos</p>
            </div>
            <Dialog open={sponsorOpen} onOpenChange={setSponsorOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateSponsor} disabled={levels.length === 0} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50" data-testid="admin-add-sponsor-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Sponsor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingSponsor ? 'Edit Sponsor' : 'New Sponsor'}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Sponsor Name</Label>
                    <Input value={sponsorForm.name} onChange={e => setSponsorForm(f => ({...f, name: e.target.value}))} placeholder="Company name" className="mt-1 rounded-xl" data-testid="admin-sponsor-name" />
                  </div>
                  <div>
                    <Label className="text-sm">Sponsorship Level</Label>
                    <Select value={sponsorForm.level_id} onValueChange={v => setSponsorForm(f => ({...f, level_id: v}))}>
                      <SelectTrigger className="mt-1 rounded-xl" data-testid="admin-sponsor-level">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {levels.map(l => {
                          const count = getSponsorCount(l.id);
                          const full = l.max_sponsors && count >= l.max_sponsors;
                          return (
                            <SelectItem key={l.id} value={l.id} disabled={full && !editingSponsor}>
                              {l.name} ({count}/{l.max_sponsors || '\u221e'}) {full ? '- FULL' : ''}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Website URL (optional)</Label>
                    <Input value={sponsorForm.website_url} onChange={e => setSponsorForm(f => ({...f, website_url: e.target.value}))} placeholder="https://..." className="mt-1 rounded-xl" data-testid="admin-sponsor-website" />
                  </div>
                  <Button onClick={handleSaveSponsor} className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-sponsor-save-btn">{editingSponsor ? 'Update' : 'Create'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {levels.length === 0 ? (
            <p className="text-stone-400 text-center py-4">Create sponsorship levels first before adding sponsors.</p>
          ) : sponsors.length === 0 ? (
            <p className="text-stone-400 text-center py-4">No sponsors yet. Click "Add Sponsor" to get started.</p>
          ) : (
            <div className="space-y-3">
              {sponsors.map(s => (
                <div key={s.id} className="flex items-center gap-4 p-4 rounded-xl bg-stone-50" data-testid={`admin-sponsor-${s.id}`}>
                  {/* Logo */}
                  <div className="w-16 h-16 rounded-xl bg-white border border-stone-200 flex items-center justify-center overflow-hidden shrink-0">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.name} className="w-full h-full object-contain p-1" />
                    ) : (
                      <Image className="w-6 h-6 text-stone-300" />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900">{s.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className="rounded-full text-xs bg-orange-100 text-orange-700">{getLevelName(s.level_id)}</Badge>
                      {s.website_url && <span className="text-xs text-stone-400 truncate">{s.website_url}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) handleUploadLogo(s.id, e.target.files[0]);
                          e.target.value = '';
                        }}
                        data-testid={`upload-input-${s.id}`}
                      />
                      <div className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-stone-100 transition-colors" data-testid={`upload-logo-${s.id}`}>
                        {uploading === s.id ? (
                          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 text-stone-400" />
                        )}
                      </div>
                    </label>
                    {s.logo_url && (
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteLogo(s.id)} className="rounded-full" data-testid={`delete-logo-${s.id}`}>
                        <X className="w-4 h-4 text-stone-400" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => openEditSponsor(s)} className="rounded-full"><Pencil className="w-4 h-4 text-stone-400" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteSponsor(s.id)} className="rounded-full"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SponsorInquiriesAdmin({ inquiries, onRefresh }) {
  const statusColors = {
    new: 'bg-orange-100 text-orange-700',
    contacted: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-emerald-100 text-emerald-700',
    declined: 'bg-stone-100 text-stone-500',
  };

  const statusIcons = {
    new: Clock,
    contacted: Mail,
    confirmed: CheckCircle,
    declined: XCircle,
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/sponsor-inquiries/${id}/status?status=${status}`);
      toast.success('Status updated');
      onRefresh();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await api.delete(`/sponsor-inquiries/${id}`);
      toast.success('Deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Card className="bg-white rounded-2xl border border-stone-100">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-stone-900">Sponsor Inquiries ({inquiries.length})</h3>
            <p className="text-xs text-stone-400 mt-0.5">Organizations interested in becoming sponsors</p>
          </div>
        </div>

        {inquiries.length === 0 ? (
          <p className="text-stone-400 text-center py-8">No sponsor inquiries yet. They will appear here when submitted via the landing page.</p>
        ) : (
          <div className="space-y-3">
            {inquiries.map(inq => {
              const StatusIcon = statusIcons[inq.status] || Clock;
              return (
                <div key={inq.id} className="p-4 rounded-xl bg-stone-50" data-testid={`inquiry-${inq.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-stone-900">{inq.company_name}</p>
                        <Badge className={`rounded-full text-xs ${statusColors[inq.status] || statusColors.new}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {inq.status}
                        </Badge>
                        {inq.interested_level && (
                          <Badge variant="outline" className="rounded-full text-xs border-stone-200">{inq.interested_level}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-600">{inq.contact_name}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-stone-500">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {inq.email}</span>
                        {inq.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {inq.phone}</span>}
                      </div>
                      {inq.message && (
                        <p className="text-sm text-stone-600 mt-2 p-2 rounded bg-white border border-stone-100">{inq.message}</p>
                      )}
                      <p className="text-[10px] text-stone-400 mt-2">
                        Submitted: {new Date(inq.created_at).toLocaleDateString()} at {new Date(inq.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Select value={inq.status} onValueChange={(v) => handleUpdateStatus(inq.id, v)}>
                        <SelectTrigger className="w-28 h-8 text-xs rounded-lg">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(inq.id)} className="text-red-500 hover:text-red-600 h-8">
                        <Trash2 className="w-3 h-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function UsersAdmin({ users, walkerTypes = [], challenges = [], teams = [], onRefresh }) {
  const [deleting, setDeleting] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [viewingActivities, setViewingActivities] = useState(null);  // walker whose activity log is open
  const emptyForm = { full_name: '', display_name: '', email: '', password: '', role: 'walker', challenge_id: '', walker_type_id: '', paid: false, extra_donation: '', new_password: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [resendingEmails, setResendingEmails] = useState(false);
  const [undoingChallenge, setUndoingChallenge] = useState(false);

  const openAdd = () => {
    setEditing(null);
    // Pre-fill role from the active filter so admins can quickly add a
    // Supporter / Admin when they've filtered the list to that role.
    const defaultRole = ['admin', 'walker', 'supporter'].includes(filterRole) ? filterRole : 'walker';
    setForm({ ...emptyForm, role: defaultRole });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      full_name: u.full_name || '',
      display_name: u.display_name || '',
      email: u.email || '',
      password: '',
      role: u.role || 'walker',
      challenge_id: u.challenge_id || '',
      walker_type_id: u.walker_type_id || '',
      paid: !!u.paid,
      extra_donation: u.extra_donation != null ? String(u.extra_donation) : '',
      new_password: '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Full Name and Email are required');
      return;
    }
    if (!editing && !form.password) {
      toast.error('Password is required for new users');
      return;
    }
    if (form.role === 'walker' && (!form.challenge_id || !form.walker_type_id)) {
      toast.error('Walkers require a Challenge and Registration Level');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        display_name: form.display_name.trim() || form.full_name.trim(),
        email: form.email.trim(),
        role: form.role,
        challenge_id: form.role === 'walker' ? form.challenge_id : null,
        walker_type_id: form.role === 'walker' ? form.walker_type_id : null,
        paid: form.role === 'walker' ? form.paid : false,
        extra_donation: form.role === 'walker' ? (parseFloat(form.extra_donation) || 0) : 0,
      };
      if (editing) {
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success('User updated');
      } else {
        payload.password = form.password;
        await api.post('/admin/users', payload);
        toast.success('User created');
      }
      setOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId, displayName) => {
    if (!window.confirm(`Delete user "${displayName}"? This will also remove their activities, pledges, and team data.`)) return;
    setDeleting(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const handleResetPassword = async () => {
    if (!editing) return;
    if (!form.new_password || form.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResettingPw(true);
    try {
      await api.post(`/admin/users/${editing.id}/reset-password`, { password: form.new_password });
      toast.success('Password reset');
      setForm({ ...form, new_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPw(false);
    }
  };

  // Admin recovery tools (Bite 3 follow-up, 2026-08). All three help
  // Sabrina heal a walker whose start-challenge / finish-email flow
  // silently failed in production without needing a curl session.
  const handleUndoStartChallenge = async () => {
    if (!editing) return;
    const ok = window.confirm(
      `Undo start-challenge for ${editing.display_name || editing.email}?\n\nThis will delete their newest participation, restore them to the prior challenge (marked 'active' again), and delete their auto-created team if they were its only member. Any activity or pledges logged on the new challenge will remain in the DB but no longer count.`
    );
    if (!ok) return;
    setUndoingChallenge(true);
    try {
      const r = await api.post(`/admin/users/${editing.id}/undo-start-challenge`);
      toast.success(`Reverted to previous challenge${r.data.deleted_team_id ? ' (team deleted)' : ''}`);
      onRefresh?.();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    } finally {
      setUndoingChallenge(false);
    }
  };

  const handleResendFinishEmails = async () => {
    if (!editing) return;
    const ok = window.confirm(
      `Resend milestone / halfway / finish emails to ${editing.display_name || editing.email}?\n\nOnly emails they never received will fire. Their per-user flags will be updated so no double-fires on future activities.`
    );
    if (!ok) return;
    setResendingEmails(true);
    try {
      const r = await api.post(`/admin/users/${editing.id}/resend-finish-emails`);
      const fired = r.data?.fired || [];
      if (fired.length === 0) {
        toast.info('Nothing to resend — all applicable emails were already sent.');
      } else {
        toast.success(`Queued ${fired.length} email(s): ${fired.join(', ')}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingEmails(false);
    }
  };

  const copyLink = (url) => {
    try {
      navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const walkerTypeById = (id) => walkerTypes.find(w => w.id === id);
  // Walkers stuck mid-onboarding (registered but never picked a challenge or
  // a registration level). These walkers show $0 / 0 km on leaderboards and
  // are easy to miss — surface them prominently so the admin can finish them.
  const incompleteOnboarding = users.filter(
    u => u.role === 'walker' && (!u.challenge_id || !u.walker_type_id)
  );
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);

  const challengeById = (id) => challenges.find(c => c.id === id);
  const teamById = (id) => teams.find(t => t.id === id);

  // Sort: Role (admin -> walker -> supporter), then alphabetical by display name
  const ROLE_ORDER = { admin: 0, walker: 1, supporter: 2 };
  const sorted = [...users].sort((a, b) => {
    const ra = ROLE_ORDER[a.role] ?? 99;
    const rb = ROLE_ORDER[b.role] ?? 99;
    if (ra !== rb) return ra - rb;
    const na = (a.display_name || a.full_name || a.email || '').toLowerCase();
    const nb = (b.display_name || b.full_name || b.email || '').toLowerCase();
    return na.localeCompare(nb);
  });
  let filtered = sorted.filter(u => filterRole === 'all' ? true : u.role === filterRole);
  if (showIncompleteOnly) {
    filtered = filtered.filter(u => u.role === 'walker' && (!u.challenge_id || !u.walker_type_id));
  }

  return (
    <Card className="bg-white rounded-2xl border border-stone-100">
      <CardContent className="p-6">
        {incompleteOnboarding.length > 0 && (
          <div
            className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex flex-wrap items-center justify-between gap-3"
            data-testid="admin-incomplete-onboarding-banner"
          >
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-amber-900">
                  {incompleteOnboarding.length} walker{incompleteOnboarding.length === 1 ? '' : 's'} with incomplete onboarding
                </p>
                <p className="text-xs text-amber-700">
                  Registered but missing a Challenge or Registration Level. They appear on the leaderboard with 0 km — click Edit to finish them.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowIncompleteOnly(v => !v); setFilterRole('walker'); }}
              className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-100 h-8"
              data-testid="admin-incomplete-onboarding-toggle"
            >
              {showIncompleteOnly ? 'Show all walkers' : 'Show only these'}
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold text-stone-900">All Users ({filtered.length})</h3>
          <div className="flex items-center gap-2">
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-36 h-9 rounded-lg" data-testid="admin-users-filter-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="walker">Walker</SelectItem>
                <SelectItem value="supporter">Supporter</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={openAdd} className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg" data-testid="admin-add-user-btn">
              <Plus className="w-4 h-4 mr-1" /> {filterRole === 'supporter' ? 'Add Supporter' : filterRole === 'admin' ? 'Add Admin' : filterRole === 'walker' ? 'Add Walker' : 'Add User'}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {filtered.map(u => {
            const wt = walkerTypeById(u.walker_type_id);
            const ch = challengeById(u.challenge_id);
            const fundraisingUrl = u.role === 'walker' ? fundraiseUrl(u) : null;
            return (
              <div key={u.id} className="p-3 rounded-xl bg-stone-50" data-testid={`admin-user-${u.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">
                      {u.display_name || u.full_name}
                      {u.fundraiser_number ? <span className="ml-2 text-xs text-stone-400">#{u.fundraiser_number}</span> : null}
                    </p>
                    <p className="text-xs text-stone-400 truncate">{u.email}</p>
                    {u.role === 'walker' && (
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500">
                        {ch && <span><Mountain className="w-3 h-3 inline -mt-0.5 mr-1" />{ch.name}</span>}
                        {wt && <span><Footprints className="w-3 h-3 inline -mt-0.5 mr-1" />{wt.name} (${wt.cost_usd || 0})</span>}
                        <span data-testid={`admin-user-team-${u.id}`}>
                          <Users className="w-3 h-3 inline -mt-0.5 mr-1" />
                          {teamById(u.team_id)?.name || <span className="text-stone-400 italic">none</span>}
                        </span>
                        <span className="text-stone-600 font-medium" data-testid={`admin-user-distance-${u.id}`}>
                          <MapPin className="w-3 h-3 inline -mt-0.5 mr-1" />{(u.total_km ?? 0).toFixed(1)} km
                        </span>
                      </div>
                    )}
                    {fundraisingUrl && (
                      <button
                        type="button"
                        onClick={() => copyLink(fundraisingUrl)}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 truncate max-w-full"
                        data-testid={`admin-user-fundraise-${u.id}`}
                        title={fundraisingUrl}
                      >
                        <Link2 className="w-3 h-3" /> {fundraisingUrl}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <Badge className={`rounded-full text-xs ${u.role === 'admin' ? 'bg-orange-100 text-orange-700' : u.role === 'supporter' ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-600'}`}>{u.role}</Badge>
                    {u.paid && <Badge className="rounded-full text-xs bg-emerald-100 text-emerald-700">paid</Badge>}
                    {u.role === 'walker' && (
                      <Button variant="ghost" size="sm" onClick={() => setViewingActivities(u)} className="h-7 w-7 p-0 text-stone-500 hover:text-orange-600" title="View activities" data-testid={`admin-view-activities-${u.id}`}>
                        <Activity className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-7 w-7 p-0 text-stone-500 hover:text-stone-800" data-testid={`admin-edit-user-${u.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {u.role !== 'admin' && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id, u.display_name || u.full_name)} disabled={deleting === u.id} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`admin-delete-user-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Full Name</Label>
                <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="admin-user-form-fullname" />
              </div>
              <div>
                <Label>Display Name</Label>
                <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="Defaults to Full Name" data-testid="admin-user-form-displayname" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="admin-user-form-email" />
              </div>
              {!editing && (
                <div>
                  <Label>Password</Label>
                  <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" data-testid="admin-user-form-password" />
                </div>
              )}
              <div>
                <Label>User Role</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="admin-user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="walker">Walker</SelectItem>
                    <SelectItem value="supporter">Supporter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Challenge {form.role === 'walker' ? '' : '(N/A)'}</Label>
                <Select
                  value={form.role === 'walker' ? (form.challenge_id || '__none__') : '__none__'}
                  onValueChange={v => setForm({ ...form, challenge_id: v === '__none__' ? '' : v })}
                  disabled={form.role !== 'walker'}
                >
                  <SelectTrigger data-testid="admin-user-form-challenge"><SelectValue placeholder="Select challenge" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {challenges.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Registration Level {form.role === 'walker' ? '' : '(N/A)'}</Label>
                <Select
                  value={form.role === 'walker' ? (form.walker_type_id || '__none__') : '__none__'}
                  onValueChange={v => setForm({ ...form, walker_type_id: v === '__none__' ? '' : v })}
                  disabled={form.role !== 'walker'}
                >
                  <SelectTrigger data-testid="admin-user-form-walkertype"><SelectValue placeholder="Select registration level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {walkerTypes.map(w => <SelectItem key={w.id} value={w.id}>{w.name} (${w.cost_usd || 0})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.role === 'walker' && (
                <div className="flex items-center justify-between rounded-lg border border-stone-200 px-3 py-2">
                  <Label className="m-0">Registration Fee Paid</Label>
                  <Switch checked={form.paid} onCheckedChange={v => setForm({ ...form, paid: v })} data-testid="admin-user-form-paid" />
                </div>
              )}
              {form.role === 'walker' && (
                <div className="rounded-lg border border-stone-200 p-3">
                  <Label>Extra donation ($)</Label>
                  <p className="text-xs text-stone-500 mt-0.5 mb-2">
                    For donations made directly to KEF during GiveButter checkout (beyond the registration fee). Added to this walker's Total Raised <em>and</em> their Team total.
                  </p>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.extra_donation}
                    onChange={(e) => setForm({ ...form, extra_donation: e.target.value })}
                    placeholder="0.00"
                    data-testid="admin-user-form-extra-donation"
                  />
                </div>
              )}
              {editing && (
                <div className="rounded-lg border border-stone-200 p-3 mt-2">
                  <Label className="text-stone-700">Reset Password</Label>
                  <p className="text-xs text-stone-500 mt-0.5 mb-2">Set a new password for <strong>{editing.email}</strong>. They will need to use this on their next sign-in.</p>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={form.new_password}
                      onChange={e => setForm({ ...form, new_password: e.target.value })}
                      placeholder="Min 6 characters"
                      data-testid="admin-user-newpw"
                    />
                    <Button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={resettingPw || !form.new_password}
                      className="bg-stone-700 hover:bg-stone-900 text-white shrink-0"
                      data-testid="admin-user-resetpw-confirm"
                    >
                      {resettingPw ? 'Resetting...' : 'Reset'}
                    </Button>
                  </div>
                </div>
              )}
              {editing && editing.role === 'walker' && (
                <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 mt-2 space-y-3">
                  <div>
                    <Label className="text-stone-700">Recovery tools</Label>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Use these only when the automated flow silently failed. Both are safe to re-run.
                    </p>
                  </div>
                  <div>
                    <Button
                      type="button"
                      onClick={handleResendFinishEmails}
                      disabled={resendingEmails}
                      className="w-full bg-white hover:bg-stone-50 text-stone-800 border border-stone-300"
                      data-testid="admin-user-resend-emails"
                    >
                      {resendingEmails ? 'Sending…' : 'Resend Milestone / Finish Emails'}
                    </Button>
                    <p className="text-[11px] text-stone-400 mt-1">
                      Only emails they never received will fire. Per-walker flags will be updated so no future double-fires.
                    </p>
                  </div>
                  <div>
                    <Button
                      type="button"
                      onClick={handleUndoStartChallenge}
                      disabled={undoingChallenge}
                      className="w-full bg-white hover:bg-red-50 text-red-700 border border-red-300"
                      data-testid="admin-user-undo-start-challenge"
                    >
                      {undoingChallenge ? 'Undoing…' : 'Undo Start Challenge (roll back to previous)'}
                    </Button>
                    <p className="text-[11px] text-stone-400 mt-1">
                      Deletes the walker&rsquo;s newest participation and restores them to the prior challenge. Auto-deletes the fresh team if they were the only member.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-user-form-save">
                {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Create User')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <UserActivitiesDialog
          walker={viewingActivities}
          onClose={() => setViewingActivities(null)}
          onChanged={onRefresh}
        />
      </CardContent>
    </Card>
  );
}

function TeamsAdmin({ teams, users = [], challenges = [], onRefresh }) {
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const copy = (text) => {
    try { navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  const walkers = users.filter(u => u.role === 'walker');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', tagline: '', challenge_id: '', leader_id: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Add-member popovers (one open at a time)
  const [memberPickerTeamId, setMemberPickerTeamId] = useState(null);
  const [memberPickerUserId, setMemberPickerUserId] = useState('');
  const [addingMember, setAddingMember] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', tagline: '', challenge_id: '', leader_id: '' });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '',
      tagline: t.tagline || '',
      challenge_id: t.challenge?.id || '',
      leader_id: t.leader?.id || '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Team name is required'); return; }
    if (!form.challenge_id) { toast.error('Challenge is required'); return; }
    if (!form.leader_id) { toast.error('Leader is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        tagline: form.tagline.trim(),
        challenge_id: form.challenge_id,
        leader_id: form.leader_id,
      };
      if (editing) {
        await api.put(`/admin/teams/${editing.id}`, payload);
        toast.success('Team updated');
      } else {
        await api.post('/admin/teams', payload);
        toast.success('Team created');
      }
      setOpen(false);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (teamId, teamName) => {
    if (!window.confirm(`Delete team "${teamName}"? Members will keep their accounts but will be removed from this team.`)) return;
    setDeleting(teamId);
    try {
      await api.delete(`/admin/teams/${teamId}`);
      toast.success('Team deleted');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete team');
    } finally {
      setDeleting(null);
    }
  };

  const handleAddMember = async (teamId) => {
    if (!memberPickerUserId) { toast.error('Pick a walker first'); return; }
    setAddingMember(true);
    try {
      await api.post(`/admin/teams/${teamId}/members`, { user_id: memberPickerUserId });
      toast.success('Member added');
      setMemberPickerTeamId(null);
      setMemberPickerUserId('');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (teamId, memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this team?`)) return;
    try {
      await api.delete(`/admin/teams/${teamId}/members/${memberId}`);
      toast.success('Member removed');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  // Walkers eligible to be ADDED to a team: must be free agents (no team_id).
  // We allow themselves if they're already on this team — but the UI filters
  // them out via the membership set below.
  const eligibleWalkersForTeam = (team) => {
    const memberIds = new Set([
      team.leader?.id,
      ...(team.members || []).map(m => m.id),
    ].filter(Boolean));
    return walkers.filter(w => !memberIds.has(w.id) && !w.team_id);
  };

  // Walkers eligible to be a LEADER of a (new or edited) team — must be unassigned.
  const eligibleLeaderWalkers = walkers.filter(w => !w.team_id || (editing && editing.leader?.id === w.id));

  return (
    <Card className="bg-white rounded-2xl border border-stone-100">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h3 className="text-lg font-bold text-stone-900">All Teams ({teams.length})</h3>
          <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg" data-testid="admin-add-team-btn">
            <Plus className="w-4 h-4 mr-1" /> New Team
          </Button>
        </div>
        <p className="text-xs text-stone-500 mb-4">
          A team stays <strong>Active</strong> until <em>both</em> of these are true:
          (1) every team member — leader and every teammate — has <em>completed</em> this challenge, <em>and</em>
          (2) the team leader has started a new challenge.
          When both are true, the team becomes <strong>Inactive</strong> (archived) and drops off the leaderboard.
        </p>
        {teams.length === 0 ? (
          <p className="text-sm text-stone-500">No teams yet.</p>
        ) : (
          <div className="space-y-3">
            {teams.map(t => {
              const inviteLink = t.invite_code ? `${origin}/teams/join/${t.invite_code}` : null;
              const eligible = eligibleWalkersForTeam(t);
              return (
                <div key={t.id} className="p-4 rounded-xl bg-stone-50" data-testid={`admin-team-${t.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-stone-900">{t.name}</p>
                      {t.tagline && <p className="text-xs text-stone-500 italic mt-0.5">"{t.tagline}"</p>}
                      <p className="text-xs text-stone-500 mt-1">
                        <Mountain className="w-3 h-3 inline -mt-0.5 mr-1" />
                        {t.challenge?.name || '—'}
                      </p>
                      <p className="text-xs text-stone-500 mt-0.5">
                        Leader: <strong>{t.leader?.display_name || t.leader?.full_name || '—'}</strong>
                        {t.leader?.email && <span className="text-stone-400"> ({t.leader.email})</span>}
                        {t.leader?.total_km != null && (
                          <span className="text-stone-500"> · <MapPin className="w-3 h-3 inline -mt-0.5 mr-0.5" />{(t.leader.total_km || 0).toFixed(1)} km</span>
                        )}
                      </p>
                      <p className="text-xs text-stone-500 mt-0.5 font-medium" data-testid={`admin-team-total-km-${t.id}`}>
                        <MapPin className="w-3 h-3 inline -mt-0.5 mr-1 text-orange-600" />
                        Team distance: <strong className="text-stone-900">{(t.total_km || 0).toFixed(1)} km</strong>
                        <span className="text-stone-400 font-normal"> (leader + all teammates)</span>
                      </p>
                      <p className="text-xs text-stone-400 mt-0.5">Created {fmtDate(t.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge
                        className={`rounded-full text-xs ${t.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}
                        data-testid={`admin-team-status-${t.id}`}
                      >
                        {t.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="h-7 w-7 p-0 text-stone-500 hover:text-stone-800" data-testid={`admin-edit-team-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id, t.name)} disabled={deleting === t.id} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`admin-delete-team-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Invite link */}
                  {inviteLink && (
                    <div className="mt-3 rounded-lg bg-white border border-stone-200 p-2.5">
                      <p className="text-xs font-medium text-stone-600 mb-1">Invite link <span className="text-stone-400 font-normal">— share with friends. When they sign up, they land here and join this team automatically.</span></p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 truncate text-xs bg-stone-100 px-2 py-1 rounded text-stone-700">{inviteLink}</code>
                        <Button size="sm" variant="ghost" onClick={() => copy(inviteLink)} className="h-7 px-2 text-stone-600 hover:text-stone-900" data-testid={`admin-team-copy-link-${t.id}`}>
                          <Link2 className="w-3.5 h-3.5 mr-1" /> Copy
                        </Button>
                      </div>
                      <p className="text-[11px] text-stone-400 mt-1">Or share the code: <code className="bg-stone-100 px-1 rounded">{t.invite_code}</code> (enter on the Join a Team step during signup).</p>
                    </div>
                  )}

                  {/* Teammates (leader excluded) */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-stone-600">
                        Team size: <span className="font-bold text-stone-900">{1 + (t.members_count || 0)}</span>
                        <span className="text-stone-400 font-normal"> (1 leader + {t.members_count || 0} teammate{t.members_count === 1 ? '' : 's'})</span>
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setMemberPickerTeamId(memberPickerTeamId === t.id ? null : t.id); setMemberPickerUserId(''); }}
                        className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700"
                        data-testid={`admin-team-add-member-${t.id}`}
                      >
                        <Plus className="w-3 h-3 mr-0.5" /> Add Teammate
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {t.members.length === 0 && <span className="text-xs text-stone-400">No teammates yet — just the leader.</span>}
                      {t.members.map(m => (
                        <span key={m.id} className="text-xs bg-white border border-stone-200 rounded-full pl-2 pr-1 py-1 flex items-center gap-1" data-testid={`admin-team-member-${t.id}-${m.id}`}>
                          {m.display_name || m.full_name}
                          {m.total_km != null && (
                            <span className="text-stone-400 font-normal" data-testid={`admin-team-member-km-${t.id}-${m.id}`}>
                              · {(m.total_km || 0).toFixed(1)} km
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(t.id, m.id, m.display_name || m.full_name)}
                            className="text-stone-400 hover:text-red-600 h-4 w-4 inline-flex items-center justify-center rounded-full"
                            data-testid={`admin-team-remove-member-${t.id}-${m.id}`}
                            aria-label="Remove member"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    {memberPickerTeamId === t.id && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-white border border-stone-200 p-2">
                        <Select value={memberPickerUserId} onValueChange={setMemberPickerUserId}>
                          <SelectTrigger className="w-64 h-8" data-testid={`admin-team-member-picker-${t.id}`}>
                            <SelectValue placeholder="Pick a walker…" />
                          </SelectTrigger>
                          <SelectContent>
                            {eligible.length === 0 && <SelectItem value="__none__" disabled>No unassigned walkers available</SelectItem>}
                            {eligible.map(w => (
                              <SelectItem key={w.id} value={w.id}>
                                {w.display_name || w.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          onClick={() => handleAddMember(t.id)}
                          disabled={addingMember || !memberPickerUserId || memberPickerUserId === '__none__'}
                          className="h-8 bg-orange-600 hover:bg-orange-700 text-white"
                          data-testid={`admin-team-confirm-add-${t.id}`}
                        >
                          {addingMember ? 'Adding…' : 'Add'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setMemberPickerTeamId(null)} className="h-8">Cancel</Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Team' : 'New Team'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Team Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="admin-team-form-name" />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} placeholder="Optional" data-testid="admin-team-form-tagline" />
              </div>
              <div>
                <Label>Challenge</Label>
                <Select value={form.challenge_id || '__none__'} onValueChange={v => setForm({ ...form, challenge_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="admin-team-form-challenge"><SelectValue placeholder="Select challenge" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select…</SelectItem>
                    {challenges.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Team Leader</Label>
                <Select value={form.leader_id || '__none__'} onValueChange={v => setForm({ ...form, leader_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="admin-team-form-leader"><SelectValue placeholder="Pick a walker" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select…</SelectItem>
                    {eligibleLeaderWalkers.length === 0 && (
                      <SelectItem value="__no_options__" disabled>No unassigned walkers available</SelectItem>
                    )}
                    {eligibleLeaderWalkers.map(w => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.display_name || w.full_name} {w.email ? `· ${w.email}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-stone-400 mt-1">Only walkers not currently on any team can be picked as a leader. To pick someone else, remove them from their current team first.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-team-form-save">
                {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Team')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function SupportersAdmin({ supporters, onRefresh }) {
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (pledge, supporterName) => {
    const walkerName = pledge.walker?.display_name || pledge.walker?.full_name || 'Walker';
    const msg =
      `Delete pledge from ${supporterName} to ${walkerName} for $${pledge.total_amount}?\n\n` +
      `This is archived to pledges_deleted (soft trail). ` +
      `The walker's Raised total and the team's Team $ will drop accordingly.`;
    if (!window.confirm(msg)) return;
    setDeletingId(pledge.pledge_id);
    try {
      await api.delete(`/admin/pledges/${pledge.pledge_id}`);
      toast.success('Pledge deleted');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete pledge');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card className="bg-white rounded-2xl border border-stone-100">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-stone-900 mb-4">All Supporters ({supporters.length})</h3>
        {supporters.length === 0 ? (
          <p className="text-sm text-stone-500">No supporters yet.</p>
        ) : (
          <div className="space-y-3">
            {supporters.map(s => {
              const supporterName = s.display_name || s.full_name;
              return (
                <div key={s.id} className="p-4 rounded-xl bg-stone-50" data-testid={`admin-supporter-${s.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-stone-900">{supporterName}</p>
                      <p className="text-xs text-stone-400">{s.email}</p>
                    </div>
                    <Badge className="rounded-full text-xs bg-emerald-100 text-emerald-700">${s.total_pledged}</Badge>
                  </div>
                  {s.pledges.length === 0 ? (
                    <p className="text-xs text-stone-400 mt-2">No pledges yet</p>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium text-stone-600">Pledges ({s.pledges.length}):</p>
                      {s.pledges.map(p => (
                        <div key={p.pledge_id} className="text-xs bg-white border border-stone-200 rounded-lg p-2 flex flex-wrap items-center justify-between gap-2" data-testid={`admin-pledge-${p.pledge_id}`}>
                          <div>
                            <p className="font-medium text-stone-900">{p.walker?.display_name || p.walker?.full_name || 'Walker'}</p>
                            <p className="text-stone-500">{p.challenge?.name || '—'} · {fmtDate(p.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="font-bold text-stone-900">${p.total_amount}</p>
                              <p className="text-stone-400 text-[10px]">
                                ${p.pledge_total || 0} flat
                                {p.pledge_per_km ? ` + $${p.pledge_per_km}/km` : ''}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(p, supporterName)}
                              disabled={deletingId === p.pledge_id}
                              className="h-7 w-7 p-0 text-stone-400 hover:text-red-600"
                              title="Delete this pledge (archived to audit log)"
                              data-testid={`admin-delete-pledge-${p.pledge_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmailAdmin() {
  const [data, setData] = useState({ configured: false, sender_email: '', templates: [], challenges: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ subject: '', body_html: '', enabled: true });
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testTo, setTestTo] = useState('');
  // Bite 3: templates are per-challenge. `selectedChallengeId` drives which
  // challenge's set the admin is editing. System-scope rows (like
  // password_reset) are ALWAYS shown at the bottom regardless of which
  // challenge is picked. Persist the picker between renders via state.
  const [selectedChallengeId, setSelectedChallengeId] = useState('');
  const [copyFromId, setCopyFromId] = useState('');
  const [copying, setCopying] = useState(false);

  const load = (challengeId) => {
    setLoading(true);
    const qs = challengeId ? `?challenge_id=${encodeURIComponent(challengeId)}` : '';
    api.get(`/admin/email-templates${qs}`)
      .then(r => {
        setData(r.data);
        if (!challengeId && !selectedChallengeId && r.data.challenges?.length) {
          // Default to first (newest) challenge on first load
          const first = r.data.challenges[0].id;
          setSelectedChallengeId(first);
          // Re-fetch scoped to that challenge so the initial view is per-challenge
          load(first);
        }
      })
      .catch(() => toast.error('Failed to load email templates'))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleChallengeChange = (v) => {
    setSelectedChallengeId(v);
    load(v);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ subject: t.subject, body_html: t.body_html, enabled: t.enabled });
    setTestTo('');
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${editing.id}`, form);
      toast.success('Template saved');
      load(selectedChallengeId);
      setEditing(null);
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!editing) return;
    setSendingTest(true);
    try {
      const res = await api.post(`/admin/email-templates/${editing.id}/test`, { to: testTo || undefined });
      toast.success(`Test email sent to ${res.data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSendingTest(false);
    }
  };

  // Move a template up/down within its scope by swapping sort_order via the
  // reorder endpoint. Simpler than full drag-and-drop and easier for
  // Sabrina to use on mobile.
  const handleMove = async (tpl, direction) => {
    // Only reorder within the same scope (challenge-scope vs system-scope)
    const sameScope = data.templates.filter(t => (
      (t.scope === 'system') === (tpl.scope === 'system')
      && (t.scope === 'system' || t.challenge_id === tpl.challenge_id)
    ));
    const idx = sameScope.findIndex(t => t.id === tpl.id);
    if (idx === -1) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sameScope.length) return;
    const reordered = [...sameScope];
    const tmp = reordered[idx];
    reordered[idx] = reordered[swap];
    reordered[swap] = tmp;
    try {
      await api.put('/admin/email-templates/reorder', {
        template_ids: reordered.map(t => t.id),
      });
      load(selectedChallengeId);
    } catch {
      toast.error('Reorder failed');
    }
  };

  const handleCopyFrom = async () => {
    if (!copyFromId || !selectedChallengeId || copyFromId === selectedChallengeId) return;
    setCopying(true);
    try {
      const r = await api.post(`/admin/challenges/${selectedChallengeId}/copy-emails-from/${copyFromId}`);
      toast.success(`Copied ${r.data.copied} template(s). ${r.data.skipped_existing} were already set on this challenge.`);
      setCopyFromId('');
      load(selectedChallengeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  if (loading) return <div className="p-6 text-stone-500 text-sm">Loading email templates...</div>;

  const currentChallenge = data.challenges?.find(c => c.id === selectedChallengeId);
  // Split into two groups so the UI renders challenge-scope first, then a
  // small "System" footer with password_reset (shared across challenges).
  const challengeTemplates = data.templates.filter(t => t.scope !== 'system');
  const systemTemplates = data.templates.filter(t => t.scope === 'system');

  return (
    <div className="space-y-4">
      <Card className="bg-white rounded-2xl border border-stone-100">
        <CardContent className="p-6 space-y-4">
          <div>
            <h3 className="text-lg font-bold text-stone-900">Email (SendGrid)</h3>
            <p className="text-sm text-stone-500 mt-1">
              Sent from <code className="bg-stone-100 px-1 rounded">{data.sender_email || '—'}</code>. Every challenge has its own set of emails — pick a challenge below, then click a template to edit.
            </p>
          </div>
          {data.configured ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
              <strong>Status: Live.</strong> SendGrid API key detected. Emails are sending.
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <strong>Status: Not Configured.</strong> Add <code>SENDGRID_API_KEY</code> to <code>backend/.env</code> to enable sends.
            </div>
          )}

          {/* Challenge picker + copy-from tool */}
          <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 space-y-3" data-testid="admin-email-challenge-picker-wrap">
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider block mb-1.5">Editing emails for challenge</label>
              <Select value={selectedChallengeId} onValueChange={handleChallengeChange}>
                <SelectTrigger className="w-full h-10 rounded-xl bg-white" data-testid="admin-email-challenge-select">
                  <SelectValue placeholder="Select a challenge…" />
                </SelectTrigger>
                <SelectContent>
                  {(data.challenges || []).map(c => (
                    <SelectItem key={c.id} value={c.id} data-testid={`admin-email-challenge-opt-${c.id}`}>
                      {c.name}{c.is_active === false ? ' (inactive)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedChallengeId && (data.challenges?.length || 0) > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-end gap-2 pt-2 border-t border-stone-200">
                <div className="flex-1">
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider block mb-1.5">Copy emails from another challenge</label>
                  <Select value={copyFromId} onValueChange={setCopyFromId}>
                    <SelectTrigger className="w-full h-10 rounded-xl bg-white" data-testid="admin-email-copy-source-select">
                      <SelectValue placeholder="Choose source challenge…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(data.challenges || [])
                        .filter(c => c.id !== selectedChallengeId)
                        .map(c => (
                          <SelectItem key={c.id} value={c.id} data-testid={`admin-email-copy-src-${c.id}`}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleCopyFrom}
                  disabled={!copyFromId || copying}
                  className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white h-10"
                  data-testid="admin-email-copy-btn"
                >
                  {copying ? 'Copying…' : 'Copy emails →'}
                </Button>
              </div>
            )}
            <p className="text-[11px] text-stone-400">
              Copy is safe: it only fills in templates you haven&apos;t already customised on <strong>{currentChallenge?.name || 'this challenge'}</strong>.
            </p>
          </div>

          {/* Per-challenge templates */}
          <div>
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">
              {currentChallenge ? `${currentChallenge.name} — ${challengeTemplates.length} templates` : 'Templates'}
            </p>
            <div className="space-y-2">
              {challengeTemplates.map((t, i) => (
                <div
                  key={t.id}
                  className="w-full p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors flex items-start justify-between gap-3"
                  data-testid={`admin-email-template-${t.key}`}
                >
                  <button
                    onClick={() => openEdit(t)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`admin-email-template-open-${t.key}`}
                  >
                    <p className="text-sm font-medium text-stone-900">{t.name}</p>
                    <p className="text-xs text-stone-500 truncate">{t.description}</p>
                    <p className="text-xs text-stone-400 mt-0.5 italic truncate">Subject: {t.subject}</p>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex flex-col">
                      <button
                        onClick={() => handleMove(t, 'up')}
                        disabled={i === 0}
                        className="text-stone-400 hover:text-stone-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                        aria-label="Move up"
                        data-testid={`admin-email-move-up-${t.key}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMove(t, 'down')}
                        disabled={i === challengeTemplates.length - 1}
                        className="text-stone-400 hover:text-stone-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                        aria-label="Move down"
                        data-testid={`admin-email-move-down-${t.key}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <Badge className={`rounded-full text-xs ${t.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}>
                      {t.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Pencil className="w-4 h-4 text-stone-400" />
                  </div>
                </div>
              ))}
              {challengeTemplates.length === 0 && (
                <p className="text-sm text-stone-400 text-center py-6">No templates yet for this challenge. Use &ldquo;Copy emails&rdquo; above to bootstrap.</p>
              )}
            </div>
          </div>

          {/* System-scope templates (shared across all challenges) */}
          {systemTemplates.length > 0 && (
            <div className="pt-4 border-t border-stone-100">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">System — shared across all challenges</p>
              <div className="space-y-2">
                {systemTemplates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => openEdit(t)}
                    className="w-full text-left p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors flex items-start justify-between gap-3"
                    data-testid={`admin-email-template-system-${t.key}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-900">{t.name}</p>
                      <p className="text-xs text-stone-500 truncate">{t.description}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`rounded-full text-xs ${t.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}>
                        {t.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      <Pencil className="w-4 h-4 text-stone-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <a
              href="https://app.sendgrid.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium"
              data-testid="admin-email-sendgrid-link"
            >
              <Send className="w-4 h-4" /> Open SendGrid Dashboard
            </a>
            <Button
              type="button"
              onClick={async () => {
                if (!window.confirm('Backfill any missing SEED templates for every active challenge?\n\nSafe — never overwrites your customised templates. Only fills in missing ones.')) return;
                try {
                  const r = await api.post('/admin/email-templates/backfill-missing');
                  const totalInserted = (r.data?.challenges || []).reduce((s, c) => s + (c.inserted || 0), 0);
                  toast.success(totalInserted === 0
                    ? 'All challenges already have a full set of templates. Nothing inserted.'
                    : `Inserted ${totalInserted} missing template row(s) across ${r.data.challenges.length} challenge(s).`);
                  load(selectedChallengeId);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Backfill failed');
                }
              }}
              className="rounded-full bg-stone-800 hover:bg-stone-900 text-white text-xs h-9 px-4"
              data-testid="admin-email-backfill-btn"
            >
              Backfill missing templates
            </Button>
          </div>

          <AchievementAlertsPanel />
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <p className="text-xs text-stone-500">{editing.description}</p>
              <div>
                <Label>Subject</Label>
                <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} data-testid="admin-email-subject" />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <textarea
                  value={form.body_html}
                  onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))}
                  rows={14}
                  className="w-full mt-1 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm font-mono text-stone-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="admin-email-body"
                />
                <p className="text-[11px] text-stone-500 mt-1">
                  Use <code className="bg-stone-100 px-1 rounded">$variable_name</code> placeholders. Available:&nbsp;
                  {(editing.merge_fields || []).map((m, i) => (
                    <span key={m}>
                      <code className="bg-stone-100 px-1 rounded">${m}</code>{i < editing.merge_fields.length - 1 ? ' · ' : ''}
                    </span>
                  ))}
                </p>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-stone-200 px-3 py-2">
                <Label className="m-0">Enabled</Label>
                <Switch checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} data-testid="admin-email-enabled" />
              </div>

              {/* Send test */}
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <p className="text-sm font-medium text-stone-800 mb-2">Send a test email</p>
                <p className="text-[11px] text-stone-500 mb-2">Renders the template with sample data and sends it to the address below. Default = your admin email.</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testTo}
                    onChange={e => setTestTo(e.target.value)}
                    placeholder="leave blank to send to your admin email"
                    data-testid="admin-email-test-to"
                  />
                  <Button
                    type="button"
                    onClick={handleSendTest}
                    disabled={sendingTest}
                    className="bg-stone-800 hover:bg-stone-900 text-white shrink-0"
                    data-testid="admin-email-test-btn"
                  >
                    <Send className="w-4 h-4 mr-1.5" /> {sendingTest ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-email-save-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AchievementAlertsPanel() {
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/achievement-alerts/pending')
      .then(r => setPending(r.data))
      .catch(() => setPending({ count: 0, events: [] }))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post('/admin/achievement-alerts/send-now');
      if (r.data.status === 'no_events') {
        toast.info('No new achievement crossings to send.');
      } else if (r.data.status === 'ok') {
        toast.success(`Digest sent to ${r.data.sent_to} admin${r.data.sent_to === 1 ? '' : 's'} (${r.data.events} crossing${r.data.events === 1 ? '' : 's'}).`);
      } else {
        toast.error(`Send failed: ${r.data.status}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-stone-100" data-testid="admin-achievement-alerts-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-base font-bold text-stone-900 flex items-center gap-2">
            <Award className="w-4 h-4 text-orange-600" /> Achievement Alerts
          </h3>
          <p className="text-xs text-stone-500 mt-1 max-w-xl">
            Automatic daily digest sent to every Admin at <strong>06:00 EST</strong>, summarising every walker who crossed a new Achievement Level (Shoes / Uniform / Life Skills / etc) in the last 24 hours.
          </p>
        </div>
        <Button
          onClick={sendNow}
          disabled={sending || loading}
          className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
          data-testid="admin-achievement-send-now"
        >
          <Send className="w-4 h-4 mr-1.5" />
          {sending ? 'Sending...' : 'Send Digest Now'}
        </Button>
      </div>
      <div className="rounded-lg bg-stone-50 border border-stone-200 p-3" data-testid="admin-achievement-pending-count">
        {loading ? (
          <p className="text-xs text-stone-400">Loading queue...</p>
        ) : !pending || pending.count === 0 ? (
          <p className="text-xs text-stone-500">No new crossings queued. Tomorrow's 06:00 EST email will be skipped if the queue is still empty.</p>
        ) : (
          <div>
            <p className="text-xs font-medium text-stone-700 mb-2">
              {pending.count} crossing{pending.count === 1 ? '' : 's'} queued for the next digest:
            </p>
            <div className="space-y-1.5">
              {pending.events.map((ev, i) => (
                <div key={i} className="text-xs bg-white border border-stone-200 rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`admin-achievement-event-${i}`}>
                  <span className="font-medium text-stone-900">{ev.walker_name}</span>
                  <span className="text-stone-600">crossed <strong>{ev.level_name}</strong> (${ev.level_amount_usd})</span>
                  <span className="text-stone-400">raised ${ev.total_raised_at_crossing}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function UserActivitiesDialog({ walker, onClose, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ km: '', steps: '', date: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const loadActivities = async () => {
    if (!walker) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${walker.id}/activities`);
      setData(r.data);
    } catch {
      setData({ user: walker, activities: [], total_km: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!walker) { setData(null); setEditingId(null); return; }
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walker]);

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({
      km: String(a.km ?? ''),
      steps: String(a.steps ?? ''),
      date: a.date ? a.date.slice(0, 10) : '',
      notes: a.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ km: '', steps: '', date: '', notes: '' });
  };

  const saveEdit = async (a) => {
    setSavingEdit(true);
    try {
      // Only send the field(s) the admin actually changed, so the server's
      // "derive the other from steps_per_km" logic kicks in correctly.
      const payload = { date: editForm.date || a.date, notes: editForm.notes };
      const kmChanged = parseFloat(editForm.km) !== parseFloat(a.km || 0);
      const stepsChanged = parseInt(editForm.steps || 0, 10) !== parseInt(a.steps || 0, 10);
      if (kmChanged) payload.km = parseFloat(editForm.km);
      if (stepsChanged) payload.steps = parseInt(editForm.steps, 10);
      // If neither changed, send both as-is so date/notes can still update
      if (!kmChanged && !stepsChanged) {
        payload.km = parseFloat(editForm.km);
        payload.steps = parseInt(editForm.steps, 10);
      }
      await api.put(`/admin/activities/${a.id}`, payload);
      toast.success('Activity updated');
      await loadActivities();
      if (onChanged) onChanged();
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (act) => {
    if (!window.confirm(`Delete this ${act.km} km activity? This cannot be undone.`)) return;
    setDeletingId(act.id);
    try {
      await api.delete(`/admin/activities/${act.id}`);
      toast.success(`Deleted ${act.km} km`);
      await loadActivities();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  if (!walker) return null;

  return (
    <Dialog open={!!walker} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-600" />
            Activities — {walker.display_name || walker.full_name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-stone-400 py-8 text-center">Loading…</p>
        ) : !data || data.activities.length === 0 ? (
          <p className="text-sm text-stone-400 py-8 text-center">No activities yet.</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 rounded-lg bg-orange-50 border border-orange-100 px-3 py-2">
              <span className="text-sm text-stone-700">
                <strong>{data.activities.length}</strong> activit{data.activities.length === 1 ? 'y' : 'ies'}
              </span>
              <span className="text-sm font-bold text-orange-700" data-testid="admin-activities-total-km">
                {(data.total_km || 0).toFixed(2)} km total
              </span>
            </div>
            <p className="text-[11px] text-stone-400 mb-2">
              Edit fixes the entry in place. If you change only km, steps are recalculated from the configured steps/km (Admin → Config). Same the other way around. To change both independently, edit both fields.
            </p>
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
              {data.activities.map(a => {
                const suspicious = (a.km || 0) > 50;
                const isEditing = editingId === a.id;
                if (isEditing) {
                  return (
                    <div key={a.id} className="rounded-lg bg-amber-50 border-2 border-amber-300 p-3 space-y-2" data-testid={`admin-activity-edit-${a.id}`}>
                      <p className="text-xs font-bold text-amber-900">Editing activity</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-stone-600">km</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={editForm.km}
                            onChange={(e) => setEditForm(f => ({ ...f, km: e.target.value }))}
                            className="h-9 mt-0.5"
                            data-testid={`admin-activity-edit-km-${a.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-stone-600">steps</Label>
                          <Input
                            type="number"
                            value={editForm.steps}
                            onChange={(e) => setEditForm(f => ({ ...f, steps: e.target.value }))}
                            className="h-9 mt-0.5"
                            data-testid={`admin-activity-edit-steps-${a.id}`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-stone-600">Date</Label>
                        <Input
                          type="date"
                          value={editForm.date}
                          onChange={(e) => setEditForm(f => ({ ...f, date: e.target.value }))}
                          className="h-9 mt-0.5"
                          data-testid={`admin-activity-edit-date-${a.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-stone-600">Notes</Label>
                        <Input
                          value={editForm.notes}
                          onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                          placeholder="Optional"
                          className="h-9 mt-0.5"
                          data-testid={`admin-activity-edit-notes-${a.id}`}
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={savingEdit}>Cancel</Button>
                        <Button size="sm" onClick={() => saveEdit(a)} disabled={savingEdit} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid={`admin-activity-save-${a.id}`}>
                          {savingEdit ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={a.id}
                    className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${suspicious ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-200'}`}
                    data-testid={`admin-activity-row-${a.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-stone-900">
                        {(a.km || 0).toFixed(2)} km
                        {a.steps ? <span className="text-stone-400 font-normal"> · {a.steps.toLocaleString()} steps</span> : null}
                        {suspicious && <span className="ml-2 text-xs font-bold text-red-700">⚠ likely typo</span>}
                      </p>
                      <p className="text-[11px] text-stone-400">
                        {a.date ? new Date(a.date).toLocaleDateString() : (a.created_at ? new Date(a.created_at).toLocaleDateString() : '—')}
                        {a.activity_type ? ` · ${a.activity_type}` : ''}
                        {a.notes ? ` · ${a.notes}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(a)}
                        className="text-stone-500 hover:text-orange-600 hover:bg-orange-50 h-8 w-8 p-0"
                        data-testid={`admin-activity-edit-btn-${a.id}`}
                        aria-label="Edit activity"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(a)}
                        disabled={deletingId === a.id}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        data-testid={`admin-activity-delete-${a.id}`}
                        aria-label="Delete activity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * One-time button that seeds challenge_participations from the current user
 * state, back-stamps challenge_id on activities/pledges/teams, and snapshots
 * finishers with final_km = challenge.total_distance_km. Idempotent.
 *
 * Lives in Admin → Stats so admins notice it on landing. After the migration
 * has been run once and looks correct, the button can be safely ignored or
 * removed in a follow-up.
 */
function MultiChallengeMigrationButton() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    if (!window.confirm(
      'Run the multi-challenge migration?\n\n' +
      'This is safe and idempotent. It creates a `challenge_participations` row ' +
      'for every walker, marks finishers as completed (final_km = challenge distance), ' +
      'and back-stamps activities/pledges/teams with challenge_id.\n\n' +
      'Re-running it does nothing on rows that already exist.'
    )) return;
    setRunning(true);
    try {
      const { data } = await api.post('/admin/migrate-multi-challenge');
      setResult(data.stats);
      toast.success('Migration complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="bg-amber-50 border border-amber-200 rounded-2xl mb-6" data-testid="multi-challenge-migration-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm font-bold text-amber-900">Multi-challenge migration</p>
            <p className="text-xs text-amber-700 mt-1">
              Seeds challenge_participations + back-stamps activities/pledges/teams.
              Marks finishers (final_km = challenge distance). Idempotent — safe to re-run.
            </p>
          </div>
          <Button
            onClick={handleRun}
            disabled={running}
            className="rounded-full bg-amber-700 hover:bg-amber-800 text-white text-sm"
            data-testid="multi-challenge-migration-btn"
          >
            {running ? 'Running...' : 'Run migration'}
          </Button>
        </div>
        {result && (
          <pre
            className="mt-3 text-[11px] font-mono bg-white border border-amber-100 rounded-lg p-3 text-stone-700 overflow-x-auto"
            data-testid="multi-challenge-migration-result"
          >
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

